import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRightIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { libraryDocumentPath, libraryTopicFolderPath } from '@/constants/appRoutes';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { ShellSidebarSlot } from '@/contexts/ShellSidebarContext';
import { ContentBreadcrumbs } from '@/features/shell/BreadcrumbBar';
import { cn } from '@/lib/utils';
import {
  ADMIN_NAV_GROUPS,
  AdminSectionNav,
  adminSectionById,
  parseAdminSection,
  type AdminSectionId,
} from './AdminSidebar';
import CrawlerSeedsSection from './CrawlerSeedsSection';
import OpinionSourcesSection from './OpinionSourcesSection';
import { StaffQuestionsSection } from './StaffQuestionsSection';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/spinner';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  adminApi,
  adminCrawlerApi,
  adminResourcesApi,
  type AdminDocumentItem,
  type AdminDocumentPatch,
  type CrawlerQueueStatus,
  type TopicItem,
  type TopicAdminItem,
  type TopicAdminType,
  type TopicCreatePayload,
  subjectRoots,
} from '../../services/api';
import { useTopics } from '../../hooks/useTopics';

// ---------------------------------------------------------------------------
// Tree helpers
// ---------------------------------------------------------------------------

interface TopicNode extends TopicAdminItem {
  children: TopicNode[];
}

function buildTree(flat: TopicAdminItem[]): TopicNode[] {
  const map = new Map<string, TopicNode>();
  flat.forEach((t) => map.set(t.id, { ...t, children: [] }));
  const roots: TopicNode[] = [];
  map.forEach((node) => {
    if (node.parent_id === null) {
      roots.push(node);
    } else {
      map.get(node.parent_id)?.children.push(node);
    }
  });
  return roots;
}

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  topic: TopicAdminItem | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: TopicAdminItem) => void;
}

function EditDialog({ topic, open, onClose, onSaved: _onSaved }: EditDialogProps) {
  const fields = [
    { label: 'Label', value: topic?.label, mono: false },
    { label: 'Slug', value: topic?.slug, mono: true },
    { label: 'Code', value: topic?.code ?? topic?.api_value, mono: true },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Tag details</DialogTitle>
          <DialogDescription>
            Fields cannot be changed. Delete this tag and recreate it to make changes.
          </DialogDescription>
        </DialogHeader>

        <div className="divide-y rounded-md border bg-muted/30 text-sm">
          {fields.map(({ label, value, mono }) => (
            <div key={label} className="flex items-center gap-4 px-4 py-2.5 cursor-not-allowed">
              <span className="w-20 shrink-0 text-xs text-muted-foreground">{label}</span>
              <span className={`flex-1 truncate text-muted-foreground select-none ${mono ? 'font-mono' : ''}`}>
                {value ?? <span className="italic">—</span>}
              </span>
              <span className="text-xs text-muted-foreground/50 shrink-0">locked</span>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// URL validation helper
// ---------------------------------------------------------------------------

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Derivation helpers
// ---------------------------------------------------------------------------

function labelToSlug(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
function labelToApiValue(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// ---------------------------------------------------------------------------
// Add dialog
// ---------------------------------------------------------------------------

interface AddDialogProps {
  open: boolean;
  defaultParentId: string | null;
  defaultType: TopicAdminType;
  onClose: () => void;
  onCreated: (topic: TopicAdminItem) => void;
}

function AddDialog({ open, defaultParentId, defaultType, onClose, onCreated }: AddDialogProps) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<TopicAdminType>(defaultType);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLabel('');
      setType(defaultType);
      setError(null);
    }
  }, [open, defaultType]);

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    const payload: TopicCreatePayload = {
      label: label.trim(),
      type,
      parent_id: defaultParentId,
      slug: labelToSlug(label) || null,
      api_value: labelToApiValue(label) || null,
    };
    try {
      const created = await adminApi.createTopic(payload);
      onCreated(created);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to create tag.');
    } finally {
      setSaving(false);
    }
  };

  const isRoot = defaultParentId === null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isRoot ? 'Add root tag' : 'Add child tag'}</DialogTitle>
          {!isRoot && (
            <DialogDescription>
              This tag will be created as a child of the selected parent.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="add-label">Label <span className="text-destructive">*</span></Label>
            <Input
              id="add-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Display label"
            />
          </div>
          {isRoot && (
            <div className="space-y-1.5">
              <Label htmlFor="add-type">Dimension</Label>
              <Select value={type} onValueChange={(v) => setType(v as TopicAdminType)}>
                <SelectTrigger id="add-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject">subject</SelectItem>
                  <SelectItem value="source">source</SelectItem>
                  <SelectItem value="rcm-cycle">rcm-cycle</SelectItem>
                  <SelectItem value="doc-kind">doc-kind</SelectItem>
                  <SelectItem value="program">program</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="rounded-md border bg-muted/40 divide-y text-sm font-mono">
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0 font-sans">slug</span>
              <span className="text-muted-foreground truncate">
                {labelToSlug(label) || <span className="italic">—</span>}
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0 font-sans">code</span>
              <span className="text-muted-foreground truncate">
                {labelToApiValue(label) || <span className="italic">—</span>}
              </span>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            className={primaryActionClass}
            onClick={handleCreate}
            disabled={saving || !label.trim()}
          >
            {saving ? <Spinner size="sm" /> : 'Create topic'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------------------

interface DeleteDialogProps {
  topic: TopicAdminItem | null;
  childCount: number;
  open: boolean;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

function DeleteDialog({ topic, childCount, open, onClose, onDeleted }: DeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleDelete = async () => {
    if (!topic) return;
    setDeleting(true);
    setError(null);
    try {
      await adminApi.deleteTopic(topic.id);
      onDeleted(topic.id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to delete topic.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete tag</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{topic?.label}</strong>?
            {childCount > 0 && (
              <span className="block mt-1 text-destructive">
                This will also delete {childCount} child {childCount === 1 ? 'topic' : 'topics'}.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Topic tree row
// ---------------------------------------------------------------------------

function countDescendants(node: TopicNode): number {
  return node.children.reduce((acc, c) => acc + 1 + countDescendants(c), 0);
}

interface TopicRowProps {
  node: TopicNode;
  depth: number;
  onEdit: (topic: TopicAdminItem) => void;
  onAdd: (parentId: string, type: TopicAdminType) => void;
  onDelete: (topic: TopicAdminItem, childCount: number) => void;
}

function TopicRow({ node, depth, onEdit, onAdd, onDelete }: TopicRowProps) {
  const [expanded, setExpanded] = useState(depth === 0);

  const hasChildren = node.children.length > 0;

  return (
    <>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md group hover:bg-muted/50 transition-colors`}
        style={{ paddingLeft: `${8 + depth * 20}px` }}
      >
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            hasChildren ? '' : 'invisible'
          } ${expanded ? 'rotate-90' : ''}`}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>

        {/* Label */}
        <span className="flex-1 text-sm truncate">{node.label}</span>

        {/* Badges */}
        {depth === 0 && (
          <Badge variant="outline" className="text-xs shrink-0">
            {node.type}
          </Badge>
        )}

        {/* Actions */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={() => onAdd(node.id, node.type)}
            className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
            title="Add child"
          >
            <PlusIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onEdit(node)}
            className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
            title="Edit"
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(node, countDescendants(node))}
            className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            title="Delete"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {expanded &&
        node.children.map((child) => (
          <TopicRow
            key={child.id}
            node={child}
            depth={depth + 1}
            onEdit={onEdit}
            onAdd={onAdd}
            onDelete={onDelete}
          />
        ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Library Documents — edit dialog (#92)
// ---------------------------------------------------------------------------

interface EditDocumentDialogProps {
  document: AdminDocumentItem | null;
  topicOptions: TopicItem[];
  open: boolean;
  onClose: () => void;
  onSaved: (updated: AdminDocumentItem) => void;
}

function EditDocumentDialog({ document: doc, topicOptions, open, onClose, onSaved }: EditDocumentDialogProps) {
  const [title, setTitle] = useState('');
  const [topicId, setTopicId] = useState('');
  const [subType, setSubType] = useState('');
  const [payer, setPayer] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (doc) {
      setTitle(doc.title ?? '');
      const explicit = doc.tags?.[0];
      const root = topicOptions.find((t) => t.code && t.code === explicit?.root_code)
        ?? topicOptions.find((t) => t.id === explicit?.id)
        ?? null;
      setTopicId(root?.id ?? explicit?.id ?? '');
      const child = root?.children?.find((c) => c.id === explicit?.id);
      setSubType(child?.slug ?? '');
      setPayer(doc.payer ?? '');
      setPolicyNumber(doc.policy_number ?? '');
      setError(null);
    }
  }, [doc]);

  const handleSave = async () => {
    if (!doc) return;
    const patch: AdminDocumentPatch = {};
    const selectedTopic = topicOptions.find((topic) => topic.id === topicId) ?? null;
    const child = selectedTopic?.children?.find((c) => c.slug === subType);
    const nextTagId = child?.id ?? (topicId || null);
    const currentTagId = doc.tags?.[0]?.id ?? null;
    if (title !== (doc.title ?? '')) patch.title = title;
    if (nextTagId !== currentTagId) patch.tag_ids = nextTagId ? [nextTagId] : [];
    if (payer !== (doc.payer ?? '')) patch.payer = payer || null;
    if (policyNumber !== (doc.policy_number ?? '')) patch.policy_number = policyNumber || null;

    setSaving(true);
    setError(null);
    try {
      const updated = await adminResourcesApi.updateDocument(doc.id, patch);
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const selectedTopic = topicOptions.find((topic) => topic.id === topicId) ?? null;
  const subTypeOptions = selectedTopic?.children?.filter((sub) => sub.slug) ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit document</DialogTitle>
          <DialogDescription>
            Update metadata for this library document. Changes take effect immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="edit-doc-title">Title</Label>
            <Input id="edit-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="edit-doc-tag">Tag</Label>
            <Select
              value={topicId}
              onValueChange={(value) => {
                setTopicId(value);
                setSubType('');
              }}
            >
              <SelectTrigger id="edit-doc-topic"><SelectValue placeholder="Select topic" /></SelectTrigger>
              <SelectContent>
                {topicOptions.map((topic) => (
                  <SelectItem key={topic.id} value={topic.id}>{topic.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="edit-doc-subtype">Child tag</Label>
            <Select
              value={subType || 'all'}
              onValueChange={(value) => setSubType(value === 'all' ? '' : value)}
              disabled={subTypeOptions.length === 0}
            >
              <SelectTrigger id="edit-doc-subtype"><SelectValue placeholder="All child tags" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subcategories</SelectItem>
                {subTypeOptions.map((sub) => (
                  <SelectItem key={sub.slug} value={sub.slug as string}>{sub.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="edit-doc-payer">Payer</Label>
            <Input id="edit-doc-payer" value={payer} onChange={(e) => setPayer(e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <Label htmlFor="edit-doc-policy">Policy number</Label>
            <Input id="edit-doc-policy" value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} placeholder="Optional" />
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className={primaryActionClass} onClick={handleSave} disabled={saving || !title.trim() || !topicId}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const primaryActionClass =
  'bg-primary text-primary-foreground shadow-none hover:bg-primary/90';

const panelClass = 'rounded-xl border border-border bg-card';
const panelTitleClass = 'font-display text-lg font-semibold tracking-tight text-primary';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<TopicAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Public topics tree (for resource upload classification)
  const { topics: publicTopics, loading: publicTopicsLoading } = useTopics();

  // Global resource upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSourceUrl, setUploadSourceUrl] = useState('');
  const [uploadSourceUrlTouched, setUploadSourceUrlTouched] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDocType, setUploadDocType] = useState<string | null>(null);
  const [uploadSubType, setUploadSubType] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<{ documentId: string; chunks: number; categorySlug: string; categoryLabel: string; subType: string; subTypeLabel: string } | null>(null);

  // Dialog state
  const [editTarget, setEditTarget] = useState<TopicAdminItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const [addParentId, setAddParentId] = useState<string | null>(null);
  const [addType, setAddType] = useState<TopicAdminType>('subject');
  const [addOpen, setAddOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<TopicAdminItem | null>(null);
  const [deleteChildCount, setDeleteChildCount] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // --- Library Documents management (#92) ---------------------------------
  const [docItems, setDocItems] = useState<AdminDocumentItem[]>([]);
  const [docTotal, setDocTotal] = useState(0);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [docSearch, setDocSearch] = useState('');
  const [docFilterTopicId, setDocFilterTopicId] = useState<string>('');
  const [docPage, setDocPage] = useState(0);
  const DOC_PAGE_SIZE = 25;
  const [editDocTarget, setEditDocTarget] = useState<AdminDocumentItem | null>(null);
  const [editDocOpen, setEditDocOpen] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  // --- Crawler Queue status (#92) ------------------------------------------
  const [queueStatus, setQueueStatus] = useState<CrawlerQueueStatus | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    setDocLoading(true);
    setDocError(null);
    try {
      const resp = await adminResourcesApi.listDocuments({
        q: docSearch || undefined,
        tag_id: docFilterTopicId || undefined,
        limit: DOC_PAGE_SIZE,
        offset: docPage * DOC_PAGE_SIZE,
      });
      setDocItems(resp.items);
      setDocTotal(resp.total);
    } catch (err: any) {
      setDocError(err?.response?.data?.detail ?? 'Failed to load documents.');
    } finally {
      setDocLoading(false);
    }
  }, [docSearch, docFilterTopicId, docPage]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const fetchQueueStatus = useCallback(async () => {
    setQueueError(null);
    try {
      const status = await adminCrawlerApi.getQueueStatus();
      setQueueStatus(status);
    } catch (err: any) {
      setQueueError(err?.response?.data?.detail ?? 'Queue status unavailable.');
    }
  }, []);

  useEffect(() => {
    fetchQueueStatus();
    const id = window.setInterval(fetchQueueStatus, 15_000);
    return () => window.clearInterval(id);
  }, [fetchQueueStatus]);

  const handleDeleteDocument = async (doc: AdminDocumentItem) => {
    const ok = window.confirm(
      `Delete this document? This removes the row and its vector chunks.\n\n"${doc.title}"`,
    );
    if (!ok) return;
    setDeletingDocId(doc.id);
    try {
      await adminResourcesApi.deleteDocument(doc.id);
      setDocItems((prev) => prev.filter((d) => d.id !== doc.id));
      setDocTotal((t) => Math.max(0, t - 1));
    } catch (err: any) {
      setDocError(err?.response?.data?.detail ?? 'Failed to delete document.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleEditDocSaved = (updated: AdminDocumentItem) => {
    setDocItems((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
  };

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await adminApi.getTopics();
      setTopics(data);
    } catch (err: any) {
      setFetchError(err?.response?.data?.detail ?? 'Failed to load topics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // ── Handlers ──

  const handleEdit = (topic: TopicAdminItem) => {
    setEditTarget(topic);
    setEditOpen(true);
  };

  const handleSaved = (updated: TopicAdminItem) => {
    setTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  const handleAdd = (parentId: string | null, type: TopicAdminType) => {
    setAddParentId(parentId);
    setAddType(type);
    setAddOpen(true);
  };

  const handleCreated = (topic: TopicAdminItem) => {
    setTopics((prev) => [...prev, topic]);
  };

  const handleDelete = (topic: TopicAdminItem, childCount: number) => {
    setDeleteTarget(topic);
    setDeleteChildCount(childCount);
    setDeleteOpen(true);
  };

  const handleDeleted = (id: string) => {
    const deletedIds = new Set<string>();
    const markDeleted = (topicId: string) => {
      deletedIds.add(topicId);
      topics.forEach((t) => {
        if (t.parent_id === topicId) markDeleted(t.id);
      });
    };
    markDeleted(id);
    setTopics((prev) => prev.filter((t) => !deletedIds.has(t.id)));
  };

  const tree = buildTree(topics);
  const facetOrder = ['subject', 'source', 'rcm-cycle', 'doc-kind', 'program'];
  const rootsByDimension = useMemo(() => {
    const map: Record<string, typeof tree> = {};
    for (const slug of facetOrder) map[slug] = [];
    for (const node of tree) {
      const key = node.type || 'subject';
      if (!map[key]) map[key] = [];
      map[key].push(node);
    }
    return map;
  }, [tree]);

  // Subject roots with codes for document upload/edit (eval-compatible)
  const rcmRootOptions = useMemo(
    () =>
      subjectRoots(publicTopics).flatMap((t) => {
        const code = t.code?.trim();
        if (!code) return [];
        return [{ ...t, code, api_value: code }];
      }),
    [publicTopics]
  );

  const allRootOptions = useMemo(
    () => subjectRoots(publicTopics),
    [publicTopics]
  );

  const labelForDocTags = useCallback((tags?: AdminDocumentItem['tags']) => {
    if (!tags?.length) return '';
    return tags.map((t) => t.label).join(', ');
  }, []);

  const selectedRoot = useMemo(
    () => rcmRootOptions.find((t) => t.id === uploadDocType) ?? null,
    [rcmRootOptions, uploadDocType]
  );

  const subTypeOptions = useMemo(
    () =>
      (selectedRoot?.children ?? []).flatMap((sub) => {
        const slug = sub.slug?.trim();
        if (!slug) return [];
        return [{ ...sub, slug }];
      }),
    [selectedRoot]
  );

  useEffect(() => {
    // Initialize topic id once public topics are loaded
    if (uploadDocType === null && rcmRootOptions.length > 0) {
      setUploadDocType(rcmRootOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcmRootOptions.length]);

  useEffect(() => {
    setUploadSubType('');
  }, [uploadDocType]);

  const handleGlobalUpload = async () => {
    if (!uploadFile) return;
    if (uploadDocType === null) return;
    if (!isValidUrl(uploadSourceUrl)) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const child = selectedRoot?.children?.find((c) => c.slug === uploadSubType);
      const tagId = child?.id ?? uploadDocType;
      const result = await adminResourcesApi.uploadGlobalPdf({
        file: uploadFile,
        source_url: uploadSourceUrl.trim(),
        title: uploadTitle.trim() || undefined,
        tag_id: tagId,
      });
      if (!result.success) {
        setUploadError(result.error || 'Upload failed.');
        return;
      }
      const successCategorySlug = selectedRoot?.slug ?? '';
      const successCategoryLabel = selectedRoot?.label ?? '';
      const successSubType = uploadSubType;
      const successSubTypeLabel =
        subTypeOptions.find((s) => s.slug === uploadSubType)?.label ?? uploadSubType;
      setUploadSuccess({
        documentId: result.document_id,
        chunks: result.chunks_written,
        categorySlug: successCategorySlug,
        categoryLabel: successCategoryLabel,
        subType: successSubType,
        subTypeLabel: successSubTypeLabel,
      });
      setUploadFile(null);
      setUploadSourceUrl('');
      setUploadSourceUrlTouched(false);
      setUploadTitle('');
      setUploadSubType('');
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail ?? 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const section = parseAdminSection(searchParams.get('section'));
  const activeSection = adminSectionById(section);
  const { setBreadcrumbSuffix } = useBreadcrumbLabel();

  const selectSection = useCallback((id: AdminSectionId) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'staff') next.delete('section');
    else next.set('section', id);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setBreadcrumbSuffix(activeSection.label);
    return () => setBreadcrumbSuffix(null);
  }, [activeSection.label, setBreadcrumbSuffix]);

  const sidebar = useMemo(
    () => <AdminSectionNav active={section} onSelect={selectSection} />,
    [section, selectSection],
  );

  return (
    <div className="app-frame app-frame--shell-layout h-full flex flex-col min-h-0">
      <ShellSidebarSlot>{sidebar}</ShellSidebarSlot>
      <div className="app-frame-inner flex flex-1 min-h-0">
        <main className="app-content-box flex flex-col min-h-0 min-w-0">
          <ContentBreadcrumbs />
          <div className="flex-1 overflow-y-auto container-scroll">
            <div className="mx-auto max-w-6xl px-5 py-5 md:px-8 md:py-7">
              <div className="mb-6 flex gap-1 overflow-x-auto lg:hidden">
                {ADMIN_NAV_GROUPS.flatMap((group) => group.items).map((item) => {
                  const selected = item.id === section;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => selectSection(item.id)}
                      className={cn(
                        'shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                        selected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
              <h1 className="font-display mb-2 text-3xl font-semibold tracking-tight text-primary">
                {activeSection.label}
              </h1>
              <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
                {activeSection.description}
              </p>

              <div>
            {section === 'staff' && <StaffQuestionsSection />}

            {section === 'upload' && (
            <div className={panelClass}>
              <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
                <div>
                  <h2 className={panelTitleClass}>Global Resource Upload</h2>
                  <p className="text-xs text-muted-foreground">
                    Upload PDFs to the shared knowledge base (used automatically in chat).
                  </p>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-upload-file">PDF file</Label>
                    <Input
                      id="admin-upload-file"
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                      disabled={uploading}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="admin-upload-url">
                      Source URL <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="admin-upload-url"
                      type="url"
                      value={uploadSourceUrl}
                      onChange={(e) => setUploadSourceUrl(e.target.value)}
                      onBlur={() => setUploadSourceUrlTouched(true)}
                      placeholder="https://example.com/document.pdf"
                      disabled={uploading}
                      className={uploadSourceUrlTouched && uploadSourceUrl && !isValidUrl(uploadSourceUrl) ? 'border-destructive focus-visible:ring-destructive' : ''}
                    />
                    {uploadSourceUrlTouched && uploadSourceUrl && !isValidUrl(uploadSourceUrl) && (
                      <p className="text-xs text-destructive">Must be a valid https:// or http:// URL.</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="admin-upload-title">Title (optional)</Label>
                    <Input
                      id="admin-upload-title"
                      value={uploadTitle}
                      onChange={(e) => setUploadTitle(e.target.value)}
                      placeholder="Leave empty to use filename"
                      disabled={uploading}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Tag</Label>
                      <Select
                        value={uploadDocType ?? ''}
                        onValueChange={(v) => setUploadDocType(v)}
                        disabled={uploading || publicTopicsLoading || rcmRootOptions.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tag" />
                        </SelectTrigger>
                        <SelectContent>
                          {rcmRootOptions.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label>Child tag (optional)</Label>
                      <Select
                        value={uploadSubType || 'all'}
                        onValueChange={(v) => setUploadSubType(v === 'all' ? '' : v.trim())}
                        disabled={uploading || subTypeOptions.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All child tags" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All subcategories</SelectItem>
                          {subTypeOptions.map((sub) => (
                            <SelectItem key={sub.slug} value={sub.slug as string}>
                              {sub.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {uploadError && (
                  <Alert variant="destructive">
                    <AlertDescription>{uploadError}</AlertDescription>
                  </Alert>
                )}

                {uploadSuccess && (
                  <Alert>
                    <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        Indexed <strong>{uploadSuccess.chunks}</strong> chunks under{' '}
                        <strong>{uploadSuccess.categoryLabel}</strong>
                        {uploadSuccess.subType && (
                          <> / <strong>{uploadSuccess.subTypeLabel}</strong></>
                        )}
                        {!uploadSuccess.subType && (
                          <span className="text-muted-foreground"> (no subcategory assigned)</span>
                        )}
                        .
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(libraryDocumentPath(uploadSuccess.documentId))}
                        >
                          View document
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const slug = uploadSuccess.categorySlug ?? 'government-regulatory';
                            const sub = uploadSuccess.subType;
                            navigate(libraryTopicFolderPath(slug, sub));
                          }}
                        >
                          View in RCM Library
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-end">
                  <Button
                    className={primaryActionClass}
                    onClick={handleGlobalUpload}
                    disabled={uploading || !uploadFile || !isValidUrl(uploadSourceUrl) || uploadDocType === null}
                  >
                    {uploading ? <Spinner size="sm" /> : 'Upload PDF'}
                  </Button>
                </div>
              </div>
            </div>
            )}

            {section === 'queue' && (
            <div className={panelClass}>
              <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
                <h2 className={panelTitleClass}>Crawler Queue</h2>
                <Button size="sm" variant="outline" onClick={fetchQueueStatus}>
                  Refresh
                </Button>
              </div>
              <div className="px-4 py-3">
                {queueError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{queueError}</AlertDescription>
                  </Alert>
                ) : queueStatus ? (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Queue name</div>
                      <div className="font-mono text-xs break-all">{queueStatus.queue_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Messages available</div>
                      <div className="font-display text-3xl font-semibold tracking-tight text-primary">{queueStatus.messages_available}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">In-flight (messages_in_flight)</div>
                      <div className="font-display text-3xl font-semibold tracking-tight text-primary">{queueStatus.messages_in_flight}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Counts auto-refresh every 15 seconds.
                </p>
              </div>
            </div>
            )}

            {section === 'seeds' && <CrawlerSeedsSection />}

            {section === 'opinions' && <OpinionSourcesSection />}

            {section === 'documents' && (
            <div className={panelClass}>
              <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
                <h2 className={panelTitleClass}>Library Documents</h2>
                <span className="text-xs text-muted-foreground">{docTotal} total</span>
              </div>
              <div className="px-4 py-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    placeholder="Search title or URL…"
                    value={docSearch}
                    onChange={(e) => {
                      setDocPage(0);
                      setDocSearch(e.target.value);
                    }}
                    className="max-w-md"
                  />
                  <Select
                    value={docFilterTopicId || '__all__'}
                    onValueChange={(v) => {
                      setDocPage(0);
                      setDocFilterTopicId(v === '__all__' ? '' : v);
                    }}
                  >
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Filter by tag" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All topics</SelectItem>
                      {allRootOptions.map((topic) => (
                        <SelectItem key={topic.id} value={topic.id}>{topic.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {docError && (
                  <Alert variant="destructive">
                    <AlertDescription>{docError}</AlertDescription>
                  </Alert>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 text-left text-xs font-medium text-muted-foreground">
                        <th className="py-2 pr-2">Title</th>
                        <th className="py-2 pr-2">Tag</th>
                        <th className="py-2 pr-2">Payer</th>
                        <th className="py-2 pr-2">Chunks</th>
                        <th className="py-2 pr-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docLoading ? (
                        <tr><td colSpan={5} className="py-6 text-center text-muted-foreground"><Spinner size="sm" /></td></tr>
                      ) : docItems.length === 0 ? (
                        <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No documents match.</td></tr>
                      ) : (
                        docItems.map((d) => (
                          <tr key={d.id} className="border-b last:border-0">
                            <td className="py-2 pr-2">
                              <div className="font-medium truncate max-w-[360px]" title={d.title}>{d.title}</div>
                              {d.source_url && (
                                <a
                                  href={d.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-muted-foreground hover:underline truncate inline-block max-w-[360px]"
                                  title={d.source_url}
                                >
                                  {d.source_url}
                                </a>
                              )}
                            </td>
                            <td className="py-2 pr-2 text-xs">
                              {labelForDocTags(d.tags) || '—'}
                            </td>
                            <td className="py-2 pr-2 text-xs">{d.payer ?? '—'}</td>
                            <td className="py-2 pr-2 text-xs">{d.chunk_count ?? '—'}</td>
                            <td className="py-2 pr-2 text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setEditDocTarget(d); setEditDocOpen(true); }}
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDocument(d)}
                                disabled={deletingDocId === d.id}
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-muted-foreground">
                    Showing {docItems.length === 0 ? 0 : docPage * DOC_PAGE_SIZE + 1}
                    –{docPage * DOC_PAGE_SIZE + docItems.length} of {docTotal}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={docPage === 0}
                      onClick={() => setDocPage((p) => Math.max(0, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={(docPage + 1) * DOC_PAGE_SIZE >= docTotal}
                      onClick={() => setDocPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            )}

            {section === 'tags' && (
            <div className={panelClass}>
              <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
                <h2 className={panelTitleClass}>Tags</h2>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(null, 'subject')}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add subject tag
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(null, 'source')}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add source tag
                  </Button>
                </div>
              </div>

              {loading && (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" />
                </div>
              )}

              {fetchError && (
                <div className="p-4">
                  <Alert variant="destructive">
                    <AlertDescription>{fetchError}</AlertDescription>
                  </Alert>
                </div>
              )}

              {!loading && !fetchError && (
                <div className="p-2">
                  {facetOrder.map((dimSlug, idx) => {
                    const roots = rootsByDimension[dimSlug] ?? [];
                    return (
                      <div key={dimSlug} className={idx > 0 ? 'mt-2 border-t pt-2' : 'mb-1'}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                          {dimSlug}
                        </p>
                        {roots.length === 0 ? (
                          <p className="text-sm text-muted-foreground px-2 pb-2">No tags yet.</p>
                        ) : (
                          roots.map((node) => (
                            <TopicRow
                              key={node.id}
                              node={node}
                              depth={0}
                              onEdit={handleEdit}
                              onAdd={handleAdd}
                              onDelete={handleDelete}
                            />
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Dialogs */}
      <EditDialog
        topic={editTarget}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />
      <AddDialog
        open={addOpen}
        defaultParentId={addParentId}
        defaultType={addType}
        onClose={() => setAddOpen(false)}
        onCreated={handleCreated}
      />
      <DeleteDialog
        topic={deleteTarget}
        childCount={deleteChildCount}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={handleDeleted}
      />
      <EditDocumentDialog
        document={editDocTarget}
        topicOptions={allRootOptions}
        open={editDocOpen}
        onClose={() => setEditDocOpen(false)}
        onSaved={handleEditDocSaved}
      />
    </div>
  );
};

export default AdminPage;
